import { Injectable } from '@angular/core';
import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, mergeMap, of, switchMap } from 'rxjs';
import { AppointmentApiService } from '../../services/appointment-api.service';
import { AppointmentActions } from './appointment.actions';

@Injectable()
export class AppointmentEffects {
  private readonly actions$ = inject(Actions);
  private readonly api = inject(AppointmentApiService);

  readonly loadAppointments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppointmentActions.loadAppointments),
      switchMap(({ admin }) =>
        (admin ? this.api.listAdmin() : this.api.list()).pipe(
          map((appointments) => AppointmentActions.loadAppointmentsSuccess({ appointments })),
          catchError((error: unknown) => of(AppointmentActions.loadAppointmentsFailure({ error: this.toMessage(error) }))),
        ),
      ),
    ),
  );

  readonly createAppointment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppointmentActions.createAppointment),
      mergeMap(({ request }) =>
        this.api.create(request).pipe(
          mergeMap((appointment) =>
            this.api.notifyWeb3Forms(request).pipe(
              map(() => AppointmentActions.createAppointmentSuccess({ appointment })),
              catchError(() => of(AppointmentActions.createAppointmentSuccess({ appointment }))),
            ),
          ),
          catchError((error: unknown) => of(AppointmentActions.createAppointmentFailure({ error: this.toMessage(error) }))),
        ),
      ),
    ),
  );

  readonly updateAppointment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppointmentActions.updateAppointment),
      mergeMap(({ id, update }) =>
        this.api.update(id, update).pipe(
          map((appointment) => AppointmentActions.updateAppointmentSuccess({ appointment })),
          catchError((error: unknown) => of(AppointmentActions.updateAppointmentFailure({ error: this.toMessage(error) }))),
        ),
      ),
    ),
  );

  private toMessage(error: unknown): string {
    if (typeof error === 'object' && error !== null && 'status' in error && error.status === 401) {
      return 'Admin authentication required. Please enter your PIN again.';
    }

    if (error instanceof Error) {
      return error.message;
    }

    return 'Something went wrong. Please try again.';
  }
}
